import { canvasLabelMeasurer } from '@ardoq/graph-labeling';
import {
  ChartDimensions,
  DEFAULT_MARGIN,
  LABEL_MARGIN,
  Margin,
  NumericRange,
  Rect,
  Size,
  TICK_LENGTH,
  getDimensions,
  labelHeight,
  rect,
  removeCollisions,
  yUnscaler,
} from '@ardoq/graph';
import type { Point } from '@ardoq/math';
import { ticks } from './ticks';
import { TITLE_MARGIN } from './consts';
import {
  formatAxisLabel,
  getBubbleChartDomains,
  getYAxisProps,
} from './calculations';
import { Locale } from '@ardoq/locale';
import {
  BubbleChartAxisProps,
  BubbleChartDataPointModel,
  BubbleChartDomains,
  BubbleChartGridMode,
  BubbleChartRenderingData,
  BubbleChartScalers,
  BubbleChartViewSettings,
  GetAxisPropsArgs,
  TickMarkProps,
} from './types';
import { fontFamily } from '@ardoq/design-tokens';

/**
 * CSS font shorthand matching the axis tick label style (Inter body text, 14px/20px).
 * Bubble chart axis labels inherit font-family from the document (Inter stack).
 */
const AXIS_LABEL_FONT = `400 14px/20px ${fontFamily.regular}`;
const measureText = canvasLabelMeasurer(AXIS_LABEL_FONT);
type LongestYTickLabelArgs = {
  windowCenter: Point;
  windowScale: number;
  clientRect: Pick<Rect, 'width' | 'height'>;
  yDomain: NumericRange;
  locale: Locale;
};
const longestYTickLabel = ({
  windowCenter,
  windowScale,
  clientRect,
  yDomain,
  locale,
}: LongestYTickLabelArgs): number => {
  const windowRect = rect(windowCenter, windowScale);

  const yUnscalerForMeasuring = yUnscaler([0, clientRect.height], yDomain, [
    windowRect.top,
    windowRect.bottom,
  ]);
  const yVisibleRangeForMeasuring: NumericRange = [
    yUnscalerForMeasuring(clientRect.height),
    yUnscalerForMeasuring(0),
  ];
  const yTicksForMeasuring = ticks(
    yDomain,
    clientRect.height,
    yVisibleRangeForMeasuring
  );

  return yTicksForMeasuring.reduce(
    (longest, tick) =>
      Math.max(longest, measureText(formatAxisLabel(tick, locale))),
    0
  );
};

type GetMarginArgs = Pick<
  BubbleChartRenderingData,
  'windowCenter' | 'windowScale' | 'domain'
> & { clientRect: Size; locale: Locale };
const getMargin = ({
  windowCenter,
  windowScale,
  clientRect,
  domain,
  locale,
}: GetMarginArgs) => ({
  left:
    longestYTickLabel({
      windowCenter,
      windowScale,
      clientRect,
      yDomain: domain.y,
      locale,
    }) +
    TICK_LENGTH +
    2 * LABEL_MARGIN +
    2 * TITLE_MARGIN +
    labelHeight,
  top: DEFAULT_MARGIN.top,
  right: DEFAULT_MARGIN.right,
  bottom: 2 * labelHeight + TICK_LENGTH + 2 * LABEL_MARGIN + 2 * TITLE_MARGIN,
});

type GetChartDimensionsArgs = GetMarginArgs & { domain: BubbleChartDomains };
const getChartDimensions = ({
  windowCenter,
  windowScale,
  clientRect,
  domain,
  locale,
}: GetChartDimensionsArgs) => {
  const margin: Margin = getMargin({
    windowCenter,
    windowScale,
    clientRect,
    domain,
    locale,
  });
  const dimensions: ChartDimensions = getDimensions(clientRect, margin);

  return {
    margin,
    dimensions,
  };
};

type GetBubbleChartExtentArgs = {
  data: BubbleChartDataPointModel[];
  viewSettings: BubbleChartViewSettings;
  clientRect: Size;
  windowCenter: Point;
  windowScale: number;
  locale: Locale;
};
export const getBubbleChartExtent = ({
  data,
  viewSettings,
  clientRect,
  windowCenter,
  windowScale,
  locale,
}: GetBubbleChartExtentArgs) => {
  const domain = getBubbleChartDomains({ data, viewSettings });
  const { margin, dimensions } = getChartDimensions({
    windowCenter,
    windowScale,
    clientRect,
    domain,
    locale,
  });

  return {
    domain,
    margin,
    dimensions,
  };
};

const getXAxisProps = ({
  margin,
  height,
  width,
  ticks,
  scaler,
  grid,
  title,
  locale,
}: GetAxisPropsArgs): BubbleChartAxisProps => {
  const gridLineLength = grid ? `${-height}` : `-${TICK_LENGTH}`;
  const arrangedTicks = ticks.map(tick => {
    const label = formatAxisLabel(tick, locale);
    return {
      position: scaler(tick),
      label,
      width: measureText(label),
    };
  });
  const decollidedTicks = removeCollisions(
    arrangedTicks,
    arrangedTick => arrangedTick.position - arrangedTick.width / 2,
    arrangedTick => arrangedTick.position + arrangedTick.width / 2
  );

  const axisLabely = TICK_LENGTH + LABEL_MARGIN;

  const tickProps = decollidedTicks.map((tick): TickMarkProps => ({
    transform: `translate(${tick.position}, 0)`,
    gridLineLength,
    tickLabel: tick.label,
    labelOffset: axisLabely,
  }));

  const axisTitleProps = {
    x: width / 2,
    y: margin.bottom - labelHeight / 2 - TITLE_MARGIN,
  };

  const axisTransform = `translate(${margin.left}, ${height + margin.top})`;
  const axisLinePath = `M0,${TICK_LENGTH}V0H${width}V${TICK_LENGTH}`;

  return {
    title,
    axisTransform,
    axisLinePath,
    tickProps,
    axisTitleProps,
  };
};

type GetBubbleChartAxisPropsArgs = {
  domain: BubbleChartDomains;
  dimensions: ChartDimensions;
  margin: Margin;
  scaleX: BubbleChartScalers['x'];
  scaleY: BubbleChartScalers['y'];
  background: BubbleChartGridMode;
  xAxisTitle: string;
  yAxisTitle: string;
  visibleRangeX: NumericRange;
  visibleRangeY: NumericRange;
  locale: Locale;
};
export const getBubbleChartAxisProps = ({
  domain,
  dimensions,
  margin,
  scaleX,
  scaleY,
  background,
  xAxisTitle,
  yAxisTitle,
  visibleRangeX,
  visibleRangeY,
  locale,
}: GetBubbleChartAxisPropsArgs) => {
  const { width, height } = dimensions.chart;

  const commonAxisArgs = {
    margin,
    height,
    width,
    grid: background === BubbleChartGridMode.GRID,
  };

  const xTicks = ticks(domain.x, dimensions.chart.width, visibleRangeX);
  const yTicks = ticks(domain.y, dimensions.chart.height, visibleRangeY);

  const xAxisProps: BubbleChartAxisProps = getXAxisProps({
    ...commonAxisArgs,
    ticks: xTicks,
    scaler: scaleX,
    title: xAxisTitle,
    locale,
  });
  const yAxisProps: BubbleChartAxisProps = getYAxisProps({
    ...commonAxisArgs,
    ticks: yTicks,
    scaler: scaleY,
    title: yAxisTitle,
    locale,
  });

  return { xAxisProps, yAxisProps };
};
